import { useEffect, useRef, useState, useCallback, lazy, Suspense, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useChatProvenance } from '../lib/useChatProvenance';
import { AnimatePresence, motion } from 'framer-motion';
import { X, RefreshCw, Send, Square, MessageSquare, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatStore } from '../stores/chatStore';
import { useTrailStore } from '../stores/trailStore';
import type { DashboardState } from '../lib/innoq';
import { useAuth } from '../hooks/useAuth';
import { FilterSuggestionPill } from './FilterSuggestionPill';
import { Tooltip } from './ui/Tooltip';
import { DevInspector } from './DevInspector';
import { MessageFeedback } from './chat/MessageFeedback';
import { pathToPageName } from '../lib/chatDirectives';
import { sanitizeUserText, Cursor, ThinkingIndicator } from '../lib/chatMarkdown';
import { useT } from '../i18n';

// KaTeX + rehype-katex + remark-math are pulled only when an assistant bubble
// renders for the first time. See lib/chatMarkdownLazy.tsx for rationale.
const ChatMarkdownBody = lazy(() => import('../lib/chatMarkdownLazy'));

// ─── Page context helper ────────────────────────────────────────────────────

// Map each static route to its existing nav.* i18n key so the "Viewing:"
// banner is localised. Detail pages resolve to a localised entity type + id.
const PAGE_LABEL_KEYS: Record<string, string> = {
  '/': 'nav.overview',
  '/members': 'nav.members',
  '/debates': 'nav.debates',
  '/bills': 'nav.bills',
  '/votes': 'nav.votes',
  '/parties': 'nav.parties',
  '/sessions': 'nav.sessions',
  '/committees': 'nav.committees',
  '/reports': 'nav.reports',
  '/usage': 'nav.usage',
  '/feedback': 'nav.feedback',
  '/chat-history': 'nav.chatHistory',
  '/settings': 'nav.settings',
};

// Returns a labelKey (static page → translate via t()) or an entity (detail
// page → "<localised type> <id>"). This drives the on-screen "Viewing:" banner;
// the machine-readable dashboard_state sent to the backend is built separately
// in buildDashboardState (the backend DOES consume page/entity/filters — see
// chat_prompt._render_dashboard_pointer).
function getPageContext(path: string): { labelKey?: string; entity?: { type: string; id: string } } {
  const detail = path.match(/^\/(\w+)\/(.+)$/);
  if (detail) {
    const [, section, id] = detail;
    const typeMap: Record<string, string> = {
      members: 'member', sessions: 'session', bills: 'bill',
      votes: 'vote', debates: 'debate', parties: 'faction',
      committees: 'committee',
    };
    // ONLY parliamentary detail pages carry a chat-actionable entity. Any other
    // detail route — notably /reports/<id> (the user's own generated research
    // reports, which the chat agent has no tool to read) — must NOT produce an
    // entity: doing so lit the "Viewing:" banner green (a false promise, since
    // the backend can't resolve it) and fed _render_dashboard_pointer a dead
    // UUID. Unknown sections fall through to the neutral generic label instead.
    const type = typeMap[section];
    return type ? { entity: { type, id } } : {};
  }
  return { labelKey: PAGE_LABEL_KEYS[path] };
}

// URL params that encode view state (tab, chart mode, sort) or chat provenance,
// not query intent — stripped before sending filters to the model so the
// dashboard-state pointer stays about *what the user is filtering for*.
const DASHBOARD_PLUMBING_KEYS = new Set(['_chat', 'tab', 'view', 'mode', 'metric', 'sort', 'sort_dir']);

// Assemble the dashboard-state pointer sent with each chat query: the current
// page, the viewed entity (with its on-screen label recovered from the
// breadcrumb trail — the detail page pushed it there when it loaded), and the
// active filters parsed from the URL. This is what lets the model resolve
// "this bill" / "he" / "these" to what the user is actually looking at.
function buildDashboardState(
  pathname: string,
  search: string,
  trail: { label: string; path: string; affair?: string | null }[],
): DashboardState {
  const page = pathToPageName(pathname);
  const ctx = getPageContext(pathname);

  let entity: DashboardState['entity'] = null;
  if (ctx.entity) {
    const crumb = trail.find(e => e.path.split('?')[0] === pathname);
    entity = { type: ctx.entity.type, id: ctx.entity.id, label: crumb?.label, affair: crumb?.affair ?? null };
  }

  const filters: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(search).entries()) {
    if (DASHBOARD_PLUMBING_KEYS.has(k) || !v) continue;
    filters[k] = v;
  }

  return { page, entity, filters };
}

// Content pages whose mere presence gives the chatbot useful domain context
// (mirrors _PAGE_CONTEXT in chat_prompt.py). Being on one of these — even with
// no filters — is enough for the assistant to understand what the user is
// looking at, so the indicator goes green. Overview is excluded on purpose (a
// KPI landing page with nothing to scope to); so are non-content routes.
const SYNCABLE_PAGES = new Set([
  'members', 'debates', 'bills', 'votes', 'parties', 'sessions', 'committees',
]);

// Whether the assembled dashboard state carries a signal the chatbot can
// actually use — i.e. whether the backend's _render_dashboard_pointer
// (chat_prompt.py) would emit a non-empty pointer for it. Drives the green
// "you can ask about this view" indicator in the context banner. Keep this in
// sync with the backend predicate:
//   • an actionable entity — member needs its NAME (label); every other type
//     (bill / session / vote / debate / faction / committee) is addressable by
//     id or label;
//   • OR at least one active filter (plumbing keys already stripped above);
//   • OR simply being on a recognised content page (domain context).
function isContextLoadBearing(state: DashboardState): boolean {
  const e = state.entity;
  if (e) {
    const type = (e.type || '').trim().toLowerCase();
    const hasLabel = !!e.label?.trim();
    const hasId = !!e.id?.trim();
    // People are keyed by name in the tools, so a member pointer is only
    // actionable with the label — matches the backend's member branch.
    if (type === 'member') return hasLabel;
    if (hasId || hasLabel) return true;
  }
  if (Object.keys(state.filters).length > 0) return true;
  return SYNCABLE_PAGES.has((state.page || '').trim().toLowerCase());
}

// ─── Welcome panel ────────────────────────────────────────────────────────────
// Shown when the chat history is empty (fresh session). The text is shown in
// all four languages at once on purpose — users land here from any UI lang
// and the multi-lingual greeting signals the assistant's data scope.

const WELCOME_LANGS: { code: string; flag: string; heading: string; body: string }[] = [
  {
    code: 'DE', flag: '🇩🇪', heading: 'Herzlich willkommen!',
    body:
      'Dies ist eine **KI-Lösung** für Recherchen zur **schweizerischen Bundespolitik**.\n\n' +
      'Die hierfür vorrangig verarbeiteten Daten stammen aus der **öffentlich zugänglichen ' +
      'Parlamentsdatenbank** *(parlament.ch)*.\n\n' +
      'Darin sind systematisch die **Geschäfte, Sessionen, Amtlichen Bulletins, Abstimmungen ' +
      'und Wahlen** der Bundesversammlung dokumentiert.',
  },
  {
    code: 'FR', flag: '🇫🇷', heading: 'Bienvenue !',
    body:
      'Il s\'agit d\'une **solution d\'IA** destinée aux recherches sur la **politique fédérale ' +
      'suisse**. Les **données** traitées en priorité à cet effet proviennent de la **base de ' +
      'données parlementaire accessible au public** *(parlament.ch)*. On y trouve une ' +
      'documentation systématique des **objets, sessions, bulletins officiels, votations et ' +
      'élections** de l\'Assemblée fédérale.',
  },
  {
    code: 'IT', flag: '🇮🇹', heading: 'Benvenuto/a!',
    body:
      'Questa è una **soluzione di IA** per ricerche sulla **politica federale svizzera**. ' +
      'Le **donneé** elaborate prioritariamente a questo scopo provengono dalla **banca dati ' +
      'parlamentare accessibile al pubblico** *(parlament.ch)*. Vi sono documentati ' +
      'sistematicamente gli **oggetti, le sessioni, i bollettini ufficiali, le votazioni e le ' +
      'elezioni** dell\'Assemblea federale.',
  },
  {
    code: 'EN', flag: '🇬🇧', heading: 'Welcome!',
    body:
      'This is an **AI solution** for research on **Swiss federal politics**. ' +
      'The **data** primarily processed for this purpose comes from the **publicly ' +
      'accessible parliamentary database** *(parlament.ch)*. It systematically documents ' +
      'the **business, sessions, official bulletins, votes and elections** of the Federal ' +
      'Assembly.',
  },
];

function WelcomeMessage() {
  return (
    <div className="flex flex-col gap-3 px-1 py-2">
      {WELCOME_LANGS.map(({ code, flag, heading, body }) => (
        <div key={code} className="flex flex-col gap-1.5">
          {/* Heading bubble — flag + welcome heading. Flag emoji renders as
              an actual flag where supported, and falls back to regional-
              indicator letters (DE/FR/IT/GB) on systems without the glyph. */}
          <div
            className="self-start max-w-[90%] px-3 py-1.5 text-[12px] text-[var(--text-2)] flex items-center gap-1.5"
            style={{ background: 'var(--surface-2)', borderRadius: '14px 14px 14px 4px' }}
          >
            <span aria-label={code}>{flag}</span>
            <span className="font-medium text-[var(--text-1)]">{heading}</span>
          </div>
          {/* Body bubble */}
          <div
            className="self-start max-w-[90%] px-3.5 py-2.5 text-[13px] leading-[1.6] text-[var(--text-1)]"
            style={{ background: 'var(--surface-2)', borderRadius: '4px 18px 18px 18px' }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p:      ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                strong: ({ children }) => <strong className="font-semibold text-[var(--text-1)]">{children}</strong>,
                em:     ({ children }) => <em className="italic text-[var(--text-2)]">{children}</em>,
              }}
            >
              {body}
            </ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ChatWidget() {
  const {
    isOpen, isWidgetEnabled, messages, isStreaming, globalFilter,
    userId, devInspectorEnabled,
    toggleWidget, setUserId, setGetToken, newSession, submitQuery, stopStreaming,
    clearGlobalFilter, submitMessageFeedback, hydrateSession,
  } = useChatStore();

  const location = useLocation();
  // True when the current page carries the chatbot filter flag (`?_chat=1`).
  // The "Dashboard filtered:" strip is gated on this so it only claims the
  // dashboard is filtered while it actually is — and clears the instant the
  // user navigates to another page (see ResearchAnchorBanner for the matching
  // top banner).
  const byChat = useChatProvenance();
  // Breadcrumb trail holds each viewed entity's on-screen label (e.g. the
  // councillor's name), which we send with the dashboard-state pointer.
  const trail = useTrailStore(s => s.trail);
  const { user, getAccessToken, isLoading: authLoading } = useAuth();
  const t = useT();
  const pageCtx = getPageContext(location.pathname);
  const pageLabel = pageCtx.labelKey
    ? t(pageCtx.labelKey)
    : pageCtx.entity
      ? `${t('chat.entity.' + pageCtx.entity.type, pageCtx.entity.type)} ${pageCtx.entity.id}`
      : t('chat.context.dashboard');

  // Green when the current view carries a signal the chatbot can act on (an
  // actionable entity or active filters) — so the user can tell at a glance
  // whether "ask about what I'm viewing" will work here. Neutral otherwise
  // (general questions still work, but there's no page-specific context).
  const contextLoadBearing = isContextLoadBearing(
    buildDashboardState(location.pathname, location.search, trail),
  );

  const [input, setInput] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  // The filter-suggestion pill the user most recently followed, as `${msgIdx}-${pillIdx}`.
  // Kept so we can highlight it and scroll it back into view after collapsing.
  const [activePill, setActivePill] = useState<string | null>(null);
  // DOM handles for the rendered pills, keyed the same way, so activation can
  // scroll the clicked pill into the (collapsed) viewport.
  const pillRefs = useRef<Map<string, HTMLElement>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Whether the viewport is parked at the bottom. While streaming we follow the
  // text only when this is true, so the user can scroll up to re-read without
  // being yanked back down on every token. A ref (not state) avoids a re-render
  // per scroll event.
  const pinnedToBottomRef = useRef(true);
  const prevMsgCountRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mark whether the user is within ~80px of the bottom of the scroll area.
  const handleMessagesScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  // Sync the Clerk user ID + JWT accessor into the chat store, then restore the
  // persisted session for a returning user. submitQuery attaches the JWT to
  // every chat fetch as `Authorization: Bearer <token>` (Audit C1 — backend
  // reads the verified `sub` instead of trusting a body field). userId stays
  // for UI-side "is signed in?" gating.
  //
  // Gated on Clerk having finished loading: `user` is briefly null during load,
  // and acting on that transient null would (a) trip the logout branch in
  // setUserId and (b) make hydrateSession see "logged out". Waiting for
  // authLoading===false means we only ever react to the settled auth state.
  useEffect(() => {
    if (authLoading) return;
    setUserId(user?.id ?? null);
    setGetToken(user?.id ? getAccessToken : null);
    // Restore the previous conversation on refresh (no-op if streaming, already
    // loaded, logged out, or the stored session isn't this user's / has
    // expired — in which case the user simply gets a fresh session).
    void hydrateSession();
  }, [authLoading, user?.id, setUserId, setGetToken, getAccessToken, hydrateSession]);

  // Note: previously a useEffect on `location.pathname` auto-called newSession()
  // when the user navigated to a different entity detail page. That fired when
  // a filter-suggestion pill in a chat answer routed to /members/<id>, wiping
  // the very chat the user was reading. Removed — dashboard_state already
  // tells the agent the current page, and chat history is persistent now, so
  // the implicit reset hurt more than it helped. Users still have the explicit
  // "new session" button.

  // Follow the conversation as it grows, but don't fight the user.
  //  - A NEW message (count grew: user submit or assistant turn start) always
  //    jumps to the bottom and re-pins.
  //  - A streaming update to the last message follows only while pinned, so
  //    scrolling up to re-read mid-generation sticks.
  useEffect(() => {
    const grew = messages.length > prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;
    if (grew) {
      pinnedToBottomRef.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (pinnedToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) setTimeout(() => textareaRef.current?.focus(), 150);
  }, [isOpen]);

  // Auto-grow the textarea up to ~8 lines, then scroll inside. Without this,
  // `rows={1}` + a tight max-height was clipping long pasted questions out of
  // view as the user typed.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
  }, [input, isOpen]);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 2500);
  };

  const handleNewSession = useCallback(() => {
    newSession();
    setActivePill(null); // messages reset — drop any stale pill highlight
    showToast(t('chat.toast.newSession'));
  }, [newSession]);

  const handleSubmit = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming || !userId) return;
    setInput('');
    submitQuery(text, buildDashboardState(location.pathname, location.search, trail));
  }, [input, isStreaming, userId, submitQuery, location.pathname, location.search, trail]);

  // Clicking a follow-up suggestion no longer fires the query. Instead we drop
  // the text into the composer so the user can edit/expand it before sending —
  // a suggestion is a starting point, not a committed query. Focus + move the
  // caret to the end so it reads as "ready to edit".
  const useFollowUp = useCallback((text: string) => {
    if (isStreaming || !userId) return;
    setInput(text);
    // Defer to next frame so the textarea has the new value before we focus
    // and place the caret at the end.
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      const end = ta.value.length;
      ta.setSelectionRange(end, end);
      ta.scrollIntoView({ block: 'nearest' });
    });
  }, [isStreaming, userId]);

  // A filter-suggestion pill was followed (it has already navigated the
  // dashboard). Collapse the panel so the destination page is visible — an
  // expanded widget covers almost the whole viewport — mark the pill active,
  // and once the collapse animation settles scroll it back into view so the
  // user sees which suggestion they followed instead of the thread jumping.
  const handlePillActivate = useCallback((key: string) => {
    setActivePill(key);
    setIsExpanded(false);
    window.setTimeout(() => {
      const el = pillRefs.current.get(key);
      if (!el) return;
      // We deliberately park away from the bottom here; don't let the next
      // streaming update yank the view back down.
      pinnedToBottomRef.current = false;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 420); // ≈ the width/height spring settle time
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // True whenever the latest assistant bubble is currently empty AND we're
  // streaming — covers both "before the first text-delta" and "between
  // iterations after we reset on a data tool call". The thinking indicator
  // is shown for the whole duration in either case.
  const isWaitingForFirst = isStreaming && messages[messages.length - 1]?.role === 'assistant' && messages[messages.length - 1]?.text === '';

  // Gate AFTER all hooks have run — Rules of Hooks forbid an early return
  // before subsequent hooks, since flipping the toggle would change the
  // hook-call order between renders and trip React's invariants.
  if (!isWidgetEnabled) return null;

  return (
    <>
      {/* Global CSS for animations */}
      <style>{`
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }

        /* Thinking shimmer — a coloured wave travels through the word.
           The gradient uses our theme red as the highlight band, fading
           into the muted text colour on either side. background-clip:text
           masks the gradient to the glyph shape. */
        .thinking-shimmer {
          background-image: linear-gradient(
            90deg,
            var(--text-3) 0%,
            var(--text-3) 35%,
            #CC0000      48%,
            #ff6b35      50%,
            #CC0000      52%,
            var(--text-3) 65%,
            var(--text-3) 100%
          );
          background-size: 250% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          -webkit-text-fill-color: transparent;
          animation: thinking-shimmer 1.8s linear infinite;
        }
        @keyframes thinking-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -100% 0; }
        }
      `}</style>

      {/* FAB button — always visible; toggles the panel */}
      <button
        onClick={toggleWidget}
        aria-label={t('chat.fab.aria')}
        className="fixed bottom-6 right-6 z-[9999] w-[52px] h-[52px] rounded-full bg-[#CC0000] text-white flex items-center justify-center shadow-lg hover:scale-[1.08] active:scale-100 transition-transform"
        style={{ boxShadow: '0 4px 20px rgba(204,0,0,0.35)' }}
      >
        <MessageSquare size={22} />
      </button>

      {/* Toast notification */}
      <AnimatePresence>
        {toastMsg && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-[88px] right-6 z-[9999] bg-[var(--surface)] border border-[var(--border)] text-[var(--text-2)] text-xs px-3 py-2 rounded-lg shadow-md font-mono"
          >
            {toastMsg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{
              opacity: 1, y: 0, scale: 1,
              // Expanded = "fill most of the screen" so the left chevron
              // gives a proper full-screen-ish reading view. Capped via
              // `calc(... - 32px)` so the panel still leaves a small
              // margin against the viewport edges (and doesn't sit
              // flush with the launcher button at the bottom-right).
              // Collapsed = the original compact widget size.
              width:  isExpanded ? 'calc(100vw - 82px)' : 450,
              height: isExpanded ? 'calc(100vh - 135px)' : 580,
            }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ opacity: { duration: 0.2 }, scale: { duration: 0.2 }, y: { duration: 0.2 }, width: { type: 'spring', stiffness: 300, damping: 30 }, height: { type: 'spring', stiffness: 300, damping: 30 } }}
            className="fixed bottom-[88px] right-6 z-[9998] flex flex-col overflow-hidden"
            style={{
              borderRadius: 16,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-4 h-14 shrink-0" style={{ background: '#CC0000' }}>
              <div className="flex items-center gap-2">
                <img src="/Swiss_Council.png" alt="Swiss Council" className="w-8 h-8 object-contain rounded-full" />
                <span className="text-[14px] font-semibold tracking-widest uppercase font-mono text-white">
                  Parlumina AI
                </span>
              </div>
              <div className="flex items-center gap-1">
                {/* Speed/Quality model selector removed (2026-07-17) — the
                    chatbot now runs a single model (Luna). The Haiku "speed"
                    path is disconnected in the backend but left in the code for
                    future re-enablement; restore this pill alongside the backend
                    routing branch in routers/chat.py to bring it back. */}
                <Tooltip content={isExpanded ? t('chat.collapse') : t('chat.expand')} width={140} className="relative inline-flex">
                  <button
                    onClick={() => setIsExpanded(e => !e)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {isExpanded ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                  </button>
                </Tooltip>
                <Tooltip content={t('chat.newSession')} width={150} className="relative inline-flex">
                  <button
                    onClick={handleNewSession}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <RefreshCw size={14} />
                  </button>
                </Tooltip>
                <Tooltip content={t('common.close')} width={120} className="relative inline-flex">
                  <button
                    onClick={toggleWidget}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </Tooltip>
              </div>
            </div>

            {/* ── Context banner ──
                Green = the current view carries context the chatbot can answer
                about (active filters or an actionable entity); neutral = only
                general questions apply here. */}
            <Tooltip
              content={contextLoadBearing ? t('chat.context.syncActive') : t('chat.context.syncInactive')}
              width={230}
              className="block"
            >
              <div
                className="flex items-center gap-1.5 px-4 h-8 shrink-0 border-b border-[var(--border)]"
                style={{ background: contextLoadBearing ? 'color-mix(in srgb, var(--success) 12%, var(--surface-2))' : 'var(--surface-2)' }}
              >
                <MapPin
                  size={11}
                  className="shrink-0"
                  style={{ color: contextLoadBearing ? 'var(--success)' : 'var(--text-3)' }}
                />
                <span
                  className="text-[11px] font-mono truncate"
                  style={{ color: contextLoadBearing ? 'var(--success)' : 'var(--text-3)' }}
                >
                  {t('chat.viewing')} {pageLabel}
                </span>
              </div>
            </Tooltip>

            {/* ── Messages ── */}
            <div
              ref={scrollContainerRef}
              onScroll={handleMessagesScroll}
              className="flex-1 overflow-y-auto px-4 py-3 space-y-2 custom-scrollbar"
            >
              {messages.length === 0 && <WelcomeMessage />}
              {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'user' ? (
                      <div
                        className="max-w-[90%] px-3.5 py-2 text-[13.5px] leading-[1.55] text-white"
                        style={{ background: '#CC0000', borderRadius: '18px 18px 4px 18px' }}
                      >
                        <ReactMarkdown
                          allowedElements={['p', 'span', 'li', 'ul', 'ol', 'strong', 'em']}
                          unwrapDisallowed
                          components={{
                            p: ({ children }) => <span className="block last:inline">{children}</span>,
                          }}
                        >
                          {sanitizeUserText(msg.text)}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="max-w-[90%] flex flex-col gap-2">
                        {/* Answer bubble */}
                        <div
                          className="px-3.5 py-2.5 text-[13.5px] text-[var(--text-1)]"
                          style={{ background: 'var(--surface-2)', borderRadius: '4px 18px 18px 18px' }}
                        >
                          {msg.text === '' && isWaitingForFirst ? (
                            <ThinkingIndicator />
                          ) : msg.stopped && !msg.text.trim() ? (
                            <span className="text-[var(--text-3)] italic">{t('chat.stopped')}</span>
                          ) : (
                            <Suspense fallback={<span className="text-[var(--text-3)]">…</span>}>
                              <ChatMarkdownBody
                                text={msg.text}
                                streaming={msg.streaming}
                                citations={msg.citations ?? []}
                                linkablePeople={msg.linkablePeople ?? []}
                              />
                            </Suspense>
                          )}
                          {msg.streaming && msg.text !== '' && <Cursor />}
                        </div>

                        {/* Filter-suggestion pills — user-clickable navigation. Click a pill
                            to jump to the filtered dashboard view. Nothing auto-applies. */}
                        {!msg.streaming && msg.filterSuggestions && msg.filterSuggestions.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {msg.filterSuggestions.map((s, si) => {
                              const pillKey = `${i}-${si}`;
                              return (
                                <div
                                  key={si}
                                  className="inline-flex"
                                  ref={el => {
                                    if (el) pillRefs.current.set(pillKey, el);
                                    else pillRefs.current.delete(pillKey);
                                  }}
                                >
                                  <FilterSuggestionPill
                                    suggestion={s}
                                    active={activePill === pillKey}
                                    onActivate={() => handlePillActivate(pillKey)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Follow-up suggestion chips */}
                        {!msg.streaming && msg.followUps && msg.followUps.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {msg.followUps.map((q, qi) => (
                              <button
                                key={qi}
                                onClick={() => useFollowUp(q)}
                                disabled={isStreaming}
                                className="text-[11px] px-2.5 py-1 rounded-full text-left transition-colors disabled:opacity-50
                                  border border-[color-mix(in_srgb,var(--accent)_28%,var(--border))]
                                  bg-[color-mix(in_srgb,var(--accent)_8%,var(--surface))]
                                  text-[var(--text-2)]
                                  hover:border-[var(--accent)]
                                  hover:bg-[color-mix(in_srgb,var(--accent)_14%,var(--surface))]
                                  hover:text-[var(--text-1)]"
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Dev inspector — only renders when debug events were received
                            (server gates on Clerk privateMetadata.role === 'developer')
                            AND the dev has not toggled the inspector off in Settings. */}
                        {devInspectorEnabled && msg.debug && msg.debug.length > 0 && (
                          <DevInspector debug={msg.debug} />
                        )}

                        {/* Feedback bar — only on completed assistant turns
                            that have a server-assigned messageId. Cap-warning
                            and edge-case `done` emissions don't carry one;
                            the feedback affordance is intentionally suppressed
                            for those so users don't rate degradation messages. */}
                        {!msg.streaming && msg.messageId && msg.text && (
                          <MessageFeedback
                            messageId={msg.messageId}
                            answerText={msg.text}
                            feedback={msg.feedback}
                            onSubmit={fb => submitMessageFeedback(msg.messageId!, fb)}
                          />
                        )}
                      </div>
                    )}
                  </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* ── Global filter strip ── */}
            {globalFilter && byChat && (
              <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-t border-amber-100 shrink-0">
                <span className="text-[11px] text-amber-700 font-mono flex-1 truncate">
                  {t('chat.dashboardFiltered')} {globalFilter}
                </span>
                <button
                  onClick={clearGlobalFilter}
                  className="text-amber-500 hover:text-amber-700 transition-colors"
                  aria-label={t('chat.clearFilter')}
                >
                  <X size={11} />
                </button>
              </div>
            )}

            {/* ── Input area ── */}
            <div className="flex items-end gap-2 px-3 py-2.5 border-t border-[var(--border)] shrink-0">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming || !userId}
                placeholder={userId ? t('chat.input.placeholder') : t('chat.input.signingIn')}
                rows={1}
                className="flex-1 resize-none bg-transparent text-[13px] text-[var(--text-1)] placeholder:text-[var(--text-3)] outline-none border-none leading-5 max-h-[180px] overflow-y-auto custom-scrollbar disabled:opacity-50"
                style={{ fontFamily: 'inherit' }}
              />
              {isStreaming ? (
                <button
                  type="button"
                  onClick={stopStreaming}
                  aria-label={t('chat.stop')}
                  title={t('chat.stop')}
                  className="w-9 h-9 rounded-full bg-[#CC0000] text-white flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity"
                >
                  <Square size={12} fill="currentColor" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!input.trim() || !userId}
                  aria-label={t('chat.send')}
                  className="w-9 h-9 rounded-full bg-[#CC0000] text-white flex items-center justify-center shrink-0 transition-opacity disabled:opacity-40"
                >
                  <Send size={14} />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
